import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Paths } from "@/paths";
import { OpLaunchPage } from "@/modules/opLaunchPages/OpLaunchPage";
import { LaunchPicker } from "@/modules/opLaunchPages/LaunchPicker";
import { show } from "@/modules/opLaunchPages/format";
import type { LaunchOption } from "@/modules/opLaunchPages/models/dashboard";
import { CommandGetLaunchesForFilter } from "@/modules/opLaunchPages/services/cmdGetLaunchesForFilter";
import { STOP_DAY } from "@/modules/opLaunchPages/dashboard/dashboardFilters";
import {
    Badge, Breadcrumb, Button, Card, EmptyState, ErrorNote, GridTable, Input, LinkButton, PageHeader, ReadOnlyTag,
    SearchField, SkeletonLines, ds, type GridColumn,
} from "@/shared/components/ds";
import type { Cell, ReportFilter, ReportResult, ReportSummary } from "./models";
import { type Sort, searchRows, sortRows, toCsv, toggleSort } from "./reportTable";
import { CommandListReports } from "./services/cmdListReports";
import { CommandRunReport } from "./services/cmdRunReport";
import styles from "./opReports.module.css";

/** Every launch, as the OP Launch Dashboard's picker lists them. */
const ALL_LAUNCHES = { revenue_type: "", engagement_manager: "-1", ops_manager: "-1" };

function initialValues(report: ReportSummary): Record<string, string> {
    return Object.fromEntries(report.filters.map((f) => [f.name, f.default ?? ""]));
}

function complete(report: ReportSummary, values: Record<string, string>): boolean {
    return report.filters.every((f) => !f.required || values[f.name]);
}

const isEmpty = (cell: Cell) => cell === null || cell === undefined || cell === "";
const isNumeric = (cell: Cell) => typeof cell === "number" || (typeof cell === "string" && /^-?\d+(\.\d+)?$/.test(cell.trim()));

/** The GridTable sort string (`3` / `-3`) from the page's Sort, and back. */
function sortString(sort: Sort | null): string | undefined {
    if (!sort) return undefined;
    return sort.direction === 1 ? String(sort.column) : `-${sort.column}`;
}

/**
 * One preserved Periscope report (MPN-2275): its parameters (only the ones the SQL cannot
 * run without), the result as a table with sortable columns, a search box over every cell,
 * and a CSV download of what is shown. The result comes down whole; nothing is paged.
 */
export default function OpReportPage() {
    const { key = "" } = useParams();
    const [report, setReport] = useState<ReportSummary | null>(null);
    const [values, setValues] = useState<Record<string, string>>({});
    const [launches, setLaunches] = useState<LaunchOption[] | null>(null);
    const [result, setResult] = useState<ReportResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState<Sort | null>(null);

    useEffect(() => {
        setReport(null);
        setResult(null);
        setError(null);
        new CommandListReports().run()
            .then((reports) => {
                const found = reports.find((r) => r.key === key);
                if (!found) {
                    setError("No such report.");
                    return;
                }
                setReport(found);
                setValues(initialValues(found));
                if (found.filters.some((f) => f.kind === "launch")) {
                    setLaunches(null);
                    new CommandGetLaunchesForFilter().run(ALL_LAUNCHES).then((r) => setLaunches(r.launches ?? [])).catch(() => setLaunches([]));
                }
            })
            .catch((e) => setError(e instanceof Error ? e.message : "Could not load the report."));
    }, [key]);

    const run = (report: ReportSummary, values: Record<string, string>) => {
        setLoading(true);
        setError(null);
        setSort(null);
        new CommandRunReport().run(report.key, values)
            .then(setResult)
            .catch((e) => setError(e instanceof Error ? e.message : "The report did not run."))
            .finally(() => setLoading(false));
    };

    // A report whose parameters are all filled (or has none) runs as soon as it is known.
    useEffect(() => {
        if (report && complete(report, initialValues(report))) run(report, initialValues(report));
    }, [report]);

    const rows = useMemo(() => sortRows(searchRows(result?.rows ?? [], search), sort), [result, search, sort]);

    const columns = useMemo<GridColumn[]>(() => {
        if (!result) return [];
        const numeric = result.columns.map((_, c) => {
            const cells = result.rows.map((r) => r[c]).filter((cell) => !isEmpty(cell));
            return cells.length > 0 && cells.every(isNumeric);
        });
        return [
            { label: "#", track: "44px", tone: "id" },
            ...result.columns.map((column, c): GridColumn => ({
                label: column, sortKey: String(c), track: "minmax(140px, 1fr)", align: numeric[c] ? "right" : "left", nowrap: numeric[c],
            })),
        ];
    }, [result]);

    const download = () => {
        if (!result) return;
        const blob = new Blob([toCsv(result.columns, rows)], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${result.key}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const title = report?.title ?? "OP Report";

    return (
        <OpLaunchPage title={title}>
            <Breadcrumb items={[{ label: "Admin" }, { label: "Old Platform" }, { label: "OP Reports", to: `/${Paths.OP_REPORTS}` }, { label: title }]} />
            <PageHeader title={title}
                actions={<Button variant="secondary" size="sm" icon={<i className="bi bi-download" aria-hidden="true" />} onClick={download} disabled={rows.length === 0}>Download CSV</Button>}>
                {report && <Badge uppercase>{report.dashboard}</Badge>}
                <ReadOnlyTag />
            </PageHeader>
            {report && report.filters.length > 0 && (
                <Card title="Parameters" clip={false}>
                    <form className={styles.params} onSubmit={(e) => { e.preventDefault(); run(report, values); }}>
                        {report.filters.map((f) => (
                            <FilterInput key={f.name} filter={f} value={values[f.name] ?? ""} launches={launches}
                                onChange={(value) => setValues({ ...values, [f.name]: value })} />
                        ))}
                        <Button type="submit" size="sm" disabled={loading || !complete(report, values)}>Run</Button>
                    </form>
                </Card>
            )}
            {error && <ErrorNote>{error}</ErrorNote>}
            {loading && <Card title="Rows"><SkeletonLines /></Card>}
            {result && !loading && (
                <Card title="Rows" count={rows.length}
                    actions={<>
                        <SearchField value={search} onChange={setSearch} placeholder="Search every column, e.g. Albion" label="Search" width={260} />
                        <span className={styles.hint}>{search ? `${rows.length} of ${result.rows.length} rows` : `${result.rows.length} rows`}</span>
                    </>}>
                    {rows.length > 0 ? (
                        <GridTable columns={columns} sort={sortString(sort)} maxHeight="calc(100vh - 320px)"
                            minWidth={Math.max(600, 44 + 160 * result.columns.length)}
                            onSort={(next) => setSort(toggleSort(sort, Number(next.replace(/^-/, ""))))}
                            rows={rows.map((row, r) => [
                                r + 1,
                                ...row.map((cell, c) => <span key={c} className={isEmpty(cell) ? ds.muted : undefined}>{show(cell)}</span>),
                            ])} />
                    ) : result.rows.length === 0 ? (
                        <EmptyState title="The report returned no rows." />
                    ) : (
                        <EmptyState title="No rows match." action={<LinkButton onClick={() => setSearch("")}>Clear the search</LinkButton>} />
                    )}
                </Card>
            )}
        </OpLaunchPage>
    );
}

function FilterInput({ filter, value, launches, onChange }: {
    filter: ReportFilter; value: string; launches: LaunchOption[] | null; onChange: (value: string) => void;
}) {
    const label = `${filter.label}${filter.required ? " *" : ""}`;
    if (filter.kind === "launch") {
        return (
            <div className={styles.paramField}>
                <span className={styles.paramLabel}>{label}</span>
                <LaunchPicker label={label} launches={launches ?? []} loading={launches === null} value={value} onPick={onChange} width={380} />
            </div>
        );
    }
    // The warehouse holds nothing after the stop day, so a date cannot be picked past it (MPN-2313).
    const max = filter.kind === "date" ? STOP_DAY : filter.kind === "month" ? STOP_DAY.slice(0, 7) : undefined;
    return (
        <Input className={styles.dateField} type={filter.kind} label={filter.label} required={filter.required} aria-label={label}
            value={value} max={max} onChange={(e) => onChange(e.target.value)} />
    );
}
